"use client";

/**
 * Button v2 — CTAs de acción.
 *
 * Variantes sólidas (primary / secondary / destructive / success / warning /
 * outline) renderizan `LiquidPill` con el mismo material que `ModalButtons`:
 * `appearance="tinted"` + `surfaceStyle="selectionSolid"`.
 *
 * ghost / link / groowCard* se mantienen como botón HTML ligero (sin vidrio).
 */

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import type { CircleActionVariant } from "./circle-action-variants";
import {
  LiquidPill,
  type LiquidPillShape,
  type LiquidPillSize,
} from "./liquid-pill";

/** Variantes que usan el material líquido de ModalButtons. */
const LIQUID_VARIANTS = new Set([
  "primary",
  "secondary",
  "destructive",
  "success",
  "warning",
  "outline",
]);

const lightButtonVariants = cva(
  [
    "group relative inline-flex items-center justify-center gap-2 overflow-hidden whitespace-nowrap font-semibold",
    "transition-all duration-300 ease-out",
    "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:ring-offset-background",
    "disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none",
    "active:scale-[0.98] active:translate-y-0",
    "select-none",
  ].join(" "),
  {
    variants: {
      variant: {
        ghost: [
          "border-0 border-transparent bg-transparent shadow-none",
          "text-foreground",
          "transition-none active:scale-100",
          "focus-visible:ring-ring/50",
        ].join(" "),
        link: [
          "bg-transparent text-primary underline-offset-4",
          "hover:text-indigo-500 hover:underline",
          "focus-visible:ring-primary/40",
        ].join(" "),
        groowCardFooter: [
          "flex w-full flex-row justify-center gap-1 border border-input bg-background px-2",
          "font-normal hover:bg-accent hover:text-accent-foreground",
        ].join(" "),
        groowCardOptions: [
          "justify-start gap-2 font-light hover:bg-accent hover:text-accent-foreground",
        ].join(" "),
        primary: "",
        secondary: "",
        destructive: "",
        success: "",
        warning: "",
        outline: "",
      },
      size: {
        xs: "h-6 px-2 text-[10px]",
        sm: "h-8 px-3 text-sm",
        md: "h-10 px-4 text-sm",
        lg: "h-11 px-5 text-base",
        xl: "h-14 px-7 text-base",
        icon: "h-8 w-8 min-w-8 px-0 font-normal",
        default: "h-10 px-4 text-sm",
      },
      shape: {
        rounded: "rounded-2xl",
        pill: "rounded-full",
        circle: "rounded-full shrink-0",
        soft: "rounded-lg",
        square: "rounded-none",
      },
      width: {
        auto: "w-auto",
        full: "w-full",
      },
    },
    defaultVariants: {
      variant: "ghost",
      size: "md",
      shape: "rounded",
      width: "auto",
    },
    compoundVariants: [
      {
        variant: "ghost",
        size: "icon",
        className: "text-slate-600 dark:text-zinc-300",
      },
      {
        variant: "ghost",
        width: "full",
        className: "justify-center font-medium text-slate-700 dark:text-zinc-200",
      },
      {
        variant: "ghost",
        shape: "circle",
        size: "sm",
        className: "h-8 w-8 min-w-8 p-0 text-slate-600 dark:text-zinc-400",
      },
      {
        shape: "circle",
        size: "icon",
        className: "rounded-full p-0",
      },
    ],
  }
);

/** Compat export — clases del path ligero (ghost/link/groow*). */
const buttonVariants = lightButtonVariants;

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof lightButtonVariants> {
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  className?: string;
  /** @deprecated rsuite compat — use width="full" */
  block?: boolean;
  /** @deprecated rsuite compat — ignored */
  appearance?: string;
  /** @deprecated rsuite compat — ignored */
  color?: string;
  /** @deprecated rsuite compat — use leftIcon */
  startIcon?: React.ReactNode;
  /** @deprecated — use onClick + router; ignored on render */
  href?: string;
  /** @deprecated shadcn quiz compat — maps to accessKey */
  accessKeyChar?: string;
}

function mapPillVariant(
  variant: ButtonProps["variant"]
): CircleActionVariant {
  switch (variant) {
    case "destructive":
      return "rose";
    case "success":
      return "emerald";
    case "warning":
      return "gold";
    case "secondary":
    case "outline":
      return "slate";
    case "primary":
    default:
      return "indigo";
  }
}

function mapPillSize(size: ButtonProps["size"]): LiquidPillSize {
  switch (size) {
    case "xs":
      return "xs";
    case "sm":
      return "sm";
    case "lg":
      return "lg";
    case "xl":
      return "xl";
    case "icon":
      return "sm";
    case "default":
    case "md":
    default:
      return "md";
  }
}

function mapPillShape(
  shape: ButtonProps["shape"],
  size: ButtonProps["size"]
): LiquidPillShape {
  if (size === "icon" || shape === "circle") return "circle";
  if (shape === "rounded" || shape === "soft") return "rounded";
  return "pill";
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      shape = "pill",
      width,
      loading = false,
      leftIcon,
      rightIcon,
      block,
      appearance: _appearance,
      color: _color,
      startIcon,
      href: _href,
      disabled,
      children,
      className,
      type = "button",
      onClick,
      form,
      name,
      value,
      id,
      accessKeyChar,
      "aria-label": ariaLabel,
      ...props
    },
    ref
  ) => {
    const resolvedLeftIcon = leftIcon ?? startIcon;
    const resolvedWidth = block ? "full" : width;
    const resolvedVariant = variant ?? "primary";
    const useLiquid = LIQUID_VARIANTS.has(resolvedVariant);
    const resolvedAccessKey = accessKeyChar ?? props.accessKey;

    const iconSize = cn(
      size === "xs" && "h-3 w-3",
      size === "sm" && "h-3.5 w-3.5",
      size === "icon" && "h-[18px] w-[18px]",
      (size === "md" || size === "default" || !size) && "h-4 w-4",
      (size === "lg" || size === "xl") && "h-5 w-5"
    );

    const content = (
      <span
        className={cn(
          "relative z-[3] inline-flex items-center justify-center gap-2",
          resolvedWidth === "full" && "w-full"
        )}
      >
        {loading ? (
          <Loader2 className={cn(iconSize, "animate-spin")} aria-hidden />
        ) : (
          resolvedLeftIcon && (
            <span className={cn(iconSize, "shrink-0")}>{resolvedLeftIcon}</span>
          )
        )}
        {children}
        {!loading && rightIcon && (
          <span className={cn(iconSize, "shrink-0")}>{rightIcon}</span>
        )}
      </span>
    );

    if (useLiquid) {
      return (
        <LiquidPill
          ref={ref as React.Ref<HTMLButtonElement>}
          as="button"
          type={type}
          variant={mapPillVariant(resolvedVariant)}
          appearance="tinted"
          surfaceStyle="selectionSolid"
          size={mapPillSize(size)}
          shape={mapPillShape(shape, size)}
          disabled={disabled || loading}
          onClick={onClick as React.MouseEventHandler<HTMLElement> | undefined}
          form={form}
          name={name}
          value={value != null ? String(value) : undefined}
          id={id}
          accessKey={resolvedAccessKey}
          aria-label={ariaLabel}
          className={cn(
            "justify-center font-semibold",
            resolvedWidth === "full" && "w-full shrink",
            className
          )}
        >
          {content}
        </LiquidPill>
      );
    }

    const showGhostHoverSurface = resolvedVariant === "ghost";

    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled || loading}
        data-v2-button=""
        data-variant={resolvedVariant}
        onClick={onClick}
        form={form}
        name={name}
        value={value}
        id={id}
        accessKey={resolvedAccessKey}
        aria-label={ariaLabel}
        className={cn(
          lightButtonVariants({
            variant: resolvedVariant,
            size,
            shape,
            width: resolvedWidth,
          }),
          className
        )}
        {...props}
      >
        {showGhostHoverSurface && (
          <span
            aria-hidden
            data-ghost-hover-surface=""
            className="pointer-events-none absolute inset-0 z-0 rounded-[inherit] bg-[var(--button-ghost-hover)] opacity-0 group-hover:opacity-100"
          />
        )}
        {content}
      </button>
    );
  }
);

Button.displayName = "Button";

export { Button, buttonVariants };
